import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useOrder, ORDER_STEPS, OrderStep } from '../context/OrderContext'

const STEP_LABELS: Record<OrderStep, string> = {
  SERVICE: 'Service',
  OPTIONS: 'Options',
  PICKUP: 'Prise en charge',
  DELIVERY: 'Livraison',
  PAYMENT: 'Paiement',
  CONFIRMATION: 'Confirmation',
}

// mapping entre steps et routes
const STEP_ROUTES: Record<OrderStep, string> = {
  SERVICE: '/order/start',
  OPTIONS: '/order/options',
  PICKUP: '/order/pickup',
  DELIVERY: '/order/delivery',
  PAYMENT: '/order/payment',
  CONFIRMATION: '/order/confirmation',
}

// Routes partenaire (tunnel raccourci: pas de pickup/delivery)
const PARTNER_STEP_ROUTES: Record<OrderStep, string> = {
  SERVICE: '/partner/book/service',
  OPTIONS: '/partner/book/options',
  PICKUP: '/partner/book/options', // pas de step pickup en partner
  DELIVERY: '/partner/book/options', // pas de step delivery en partner
  PAYMENT: '/partner/book/payment',
  CONFIRMATION: '/order/confirmation',
}

// Steps affichés en mode partenaire (tunnel raccourci)
const PARTNER_VISIBLE_STEPS: OrderStep[] = ['SERVICE', 'OPTIONS', 'PAYMENT', 'CONFIRMATION']

const Stepper: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { currentStep, maxStepReached, goToStep, canGoToStep, isPartnerMode } = useOrder()

  const isPartnerRoute = location.pathname.startsWith('/partner/book')
  const usePartnerSteps = isPartnerMode || isPartnerRoute

  const visibleSteps = usePartnerSteps ? PARTNER_VISIBLE_STEPS : ORDER_STEPS
  const routes = usePartnerSteps ? PARTNER_STEP_ROUTES : STEP_ROUTES

  const stepIndex = (step: OrderStep) => ORDER_STEPS.indexOf(step)

  const handleStepClick = (step: OrderStep) => {
    if (!canGoToStep(step)) return
    goToStep(step)
    navigate(routes[step])
  }

  return (
    <nav
      aria-label="Progression de la commande"
      style={{
        display: 'flex',
        flexWrap: 'wrap',           // ✅ permet de passer sur plusieurs lignes
        gap: '0.5rem',
        marginBottom: '1.5rem',
      }}
    >
      {visibleSteps.map((step, index) => {
        const isActive = step === currentStep
        const isClickable = canGoToStep(step)
        const isDone = ORDER_STEPS.indexOf(step) < ORDER_STEPS.indexOf(maxStepReached)

        return (
          <button
            key={step}
            type="button"
            data-testid={`step-${step}`}
            onClick={() => handleStepClick(step)}
            disabled={!isClickable}
            style={{
              flex: '1 1 120px',           // ✅ s’adapte à la largeur dispo
              minWidth: '110px',           // ✅ taille mini sur mobile
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '.4rem',
              padding: '.4rem .6rem',
              borderRadius: '999px',
              border: isActive
                ? '2px solid #22c55e'
                : '1px solid rgba(148,163,184,0.5)',
              background: isActive ? 'rgba(34,197,94,0.1)' : 'transparent',
              color: isClickable ? 'var(--tun-step-text)' : 'var(--tun-step-disabled)',
              cursor: isClickable ? 'pointer' : 'default',
              fontSize: '.8rem',
              whiteSpace: 'nowrap',
            }}
          >
            <span
              style={{
                width: '20px',
                height: '20px',
                borderRadius: '999px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '.75rem',
                background: isDone ? '#22c55e' : 'var(--tun-step-bubble-bg)',
                color: isDone ? '#020617' : 'var(--tun-step-bubble-text)',
                flexShrink: 0,
              }}
            >
              {index + 1}
            </span>
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}
            >
              {STEP_LABELS[step]}
            </span>
          </button>
        )
      })}
    </nav>
  )
}

export default Stepper
